/**
 * The level line (rubber band) on a sound clip (1.7.4, docs/dev/editor-1.7.4-build.md "Audio levels"): the clip's
 * level over its length (its gain plus its volume keys), drawn in the clip's box. ⌘-click on the line adds a key
 * there; drag a key up, down and sideways; a selected key goes with Delete (the Timeline's key listener); dragging the
 * line itself changes the clip's level (gainDb), so the whole line moves with its keys. A readout shows the level
 * heard (the clip's level plus its track's gain), "−6.0 dB". Each drag is one undo step (coalesced, sealed on release).
 */
import { useLayoutEffect, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { TICKS_PER_SECOND, ops, ticksPerFrame, volumeAt, type Rational, type SequenceDocument, type VolumeKey } from '@prism/contracts';
import { dbToY, formatDb, levelPath, sourceTicksAt, yToDb } from './levelScale';

export interface LevelClip { id: string; gainDb: number; volumeKeys?: readonly VolumeKey[]; sourceInTicks: number; sourceOutTicks: number }

type Drag =
  | { kind: 'line'; startY: number; startGain: number; db: number }
  | { kind: 'key'; index: number; atTicks: number; db: number };

export function LevelLine({ clip, trackGain, pps, rate, canEdit, apply, seal, selectedKey, onSelectKey }: {
  clip: LevelClip;
  /** The track's gain (dB), added to the readout. */
  trackGain: number;
  pps: number;
  rate: Rational;
  canEdit: boolean;
  apply: (fn: (d: SequenceDocument) => SequenceDocument, coalesce?: string) => void;
  seal?: () => void;
  /** The selected key of this clip (its index), for Delete. */
  selectedKey: number | null;
  onSelectKey: (index: number | null) => void;
}) {
  const svg = useRef<SVGSVGElement>(null);
  const [box, setBox] = useState({ w: 0, h: 24 });
  const [drag, setDrag] = useState<Drag | null>(null);
  const [hover, setHover] = useState<{ x: number; db: number } | null>(null);
  useLayoutEffect(() => {
    const el = svg.current;
    if (!el) return;
    const measure = () => setBox((b) => (b.w === el.clientWidth && b.h === el.clientHeight ? b : { w: el.clientWidth, h: el.clientHeight || 24 }));
    measure();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  const h = box.h;
  const local = (e: { clientX: number; clientY: number }) => {
    const r = svg.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const keys = clip.volumeKeys ?? [];
  const tpf = ticksPerFrame(rate);
  const keyX = (k: VolumeKey) => ((k.atTicks - clip.sourceInTicks) / TICKS_PER_SECOND) * pps;
  const total = (db: number) => db + trackGain;

  const onLineDown = (e: RPointerEvent<SVGElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    if (!canEdit) return;
    const p = local(e);
    const t = Math.min(clip.sourceOutTicks, Math.max(clip.sourceInTicks, Math.round(sourceTicksAt(clip, p.x, pps) / tpf) * tpf));
    if (e.metaKey || e.ctrlKey) {
      // A key on the line where it is: its offset over the clip's gain.
      const db = volumeAt(clip, t) - clip.gainDb;
      const id = clip.id;
      seal?.();
      apply((d) => ops.addVolumeKey(d, id, { atTicks: t, db }));
      seal?.();
      const index = [...keys.filter((k) => k.atTicks !== t), { atTicks: t, db }].sort((a, b) => a.atTicks - b.atTicks).findIndex((k) => k.atTicks === t);
      onSelectKey(index);
      return;
    }
    (e.target as Element).setPointerCapture(e.pointerId);
    onSelectKey(null);
    setDrag({ kind: 'line', startY: p.y, startGain: clip.gainDb, db: volumeAt(clip, t) });
  };
  const onKeyDown = (e: RPointerEvent<SVGElement>, index: number) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    onSelectKey(index);
    if (!canEdit) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    const k = keys[index]!;
    setDrag({ kind: 'key', index, atTicks: k.atTicks, db: volumeAt(clip, k.atTicks) });
  };
  const onMove = (e: RPointerEvent<SVGElement>) => {
    if (!drag) {
      const p = local(e);
      setHover({ x: p.x, db: volumeAt(clip, sourceTicksAt(clip, p.x, pps)) });
      return;
    }
    const p = local(e);
    const id = clip.id;
    if (drag.kind === 'line') {
      // The level under the pointer follows it: the gain changes by the same amount.
      const want = yToDb(dbToY(drag.db, h) + (p.y - drag.startY), h);
      const gain = drag.startGain + (want - drag.db);
      apply((d) => ops.setClipLevel(d, id, gain), `level:${id}`);
      setHover({ x: p.x, db: want });
    } else {
      const at = Math.round(sourceTicksAt(clip, p.x, pps) / tpf) * tpf;
      const level = yToDb(p.y, h);
      const index = drag.index;
      apply((d) => ops.moveVolumeKey(d, id, index, { atTicks: at, db: level - clip.gainDb }), `vkey:${id}:${index}`);
      setHover({ x: p.x, db: level });
    }
  };
  const onUp = () => {
    if (drag) { setDrag(null); seal?.(); }
  };

  const readout = drag || hover;
  return (
    <svg ref={svg} className={`tl-level ${drag ? 'dragging' : ''}`} data-testid="level-line" data-clip={clip.id} data-db={clip.gainDb}
      onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onPointerLeave={() => { if (!drag) setHover(null); }}
      preserveAspectRatio="none" aria-label={`Level ${formatDb(total(clip.gainDb))}`}>
      <polyline className="tl-level-line" points={levelPath(clip, pps, h)} />
      {canEdit && <polyline className="tl-level-hit" points={levelPath(clip, pps, h)} onPointerDown={onLineDown} onPointerLeave={() => { if (!drag) setHover(null); }}><title>Drag to change the level · ⌘-click to add a key</title></polyline>}
      {keys.map((k, i) => (
        <circle key={i} className={`tl-volume-key ${selectedKey === i ? 'selected' : ''}`} cx={keyX(k)} cy={dbToY(volumeAt(clip, k.atTicks), h)} r={selectedKey === i ? 4 : 3.2}
          data-testid="volume-key" data-clip={clip.id} data-index={i} data-db={k.db} onPointerDown={(e) => onKeyDown(e, i)}>
          <title>{formatDb(total(volumeAt(clip, k.atTicks)))}{canEdit ? ' · drag to move, Delete removes it' : ''}</title>
        </circle>
      ))}
      {readout && (
        <foreignObject x={Math.max(0, Math.min(box.w - 64, (hover?.x ?? 0) + 6))} y={0} width={64} height={14} className="tl-level-readout-box">
          <span className="tl-level-readout mono" data-testid="level-readout">{formatDb(total(drag ? (hover?.db ?? drag.db) : hover!.db))}</span>
        </foreignObject>
      )}
    </svg>
  );
}
